import { Button, Col, Descriptions, Form, Input, Popconfirm, Row, Space, Table, Tag, Typography, notification } from 'antd';
import { SearchOutlined, UnlockOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { SensitiveRiskUserRecord, User } from '../../api/go';

type QueryForm = {
  user_id?: string;
};

const riskTypeText: Record<string, string> = {
  POLITICAL: '涉政',
  PORN: '色情',
  ABUSE: '辱骂',
  AD: '广告',
  OTHER: '其他',
};

const riskLevelText: Record<number, { text: string; color: string }> = {
  1: { text: '低', color: 'green' },
  2: { text: '中', color: 'orange' },
  3: { text: '高', color: 'red' },
};

const statusText: Record<number, { text: string; color: string }> = {
  1: { text: '观察中', color: 'blue' },
  2: { text: '已禁言', color: 'red' },
  3: { text: '已解除', color: 'green' },
  4: { text: '已封禁', color: 'volcano' },
};

const handleStatusText: Record<number, { text: string; color: string }> = {
  0: { text: '待处理', color: 'orange' },
  1: { text: '已处理', color: 'green' },
  2: { text: '忽略', color: 'default' },
};

function formatDate(value?: string | null) {
  if (!value) {
    return '-';
  }

  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

function nullableText(value?: string | number | null) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  return value;
}

function renderDictTag(value: number | null, dict: Record<number, { text: string; color: string }>) {
  if (value === null || value === undefined) {
    return '-';
  }

  const item = dict[value];
  if (!item) {
    return value;
  }

  return <Tag color={item.color}>{item.text}</Tag>;
}

const columns: ColumnsType<SensitiveRiskUserRecord> = [
  {
    title: '风险类型',
    dataIndex: 'risk_type',
    key: 'risk_type',
    width: 160,
    render: (value: string) => (
      <Space>
        <Tag>{value}</Tag>
        <span>{riskTypeText[value] || '-'}</span>
      </Space>
    ),
  },
  {
    title: '风险等级',
    dataIndex: 'risk_level',
    key: 'risk_level',
    width: 110,
    render: (value: number | null) => renderDictTag(value, riskLevelText),
  },
  {
    title: '命中次数',
    dataIndex: 'hit_count',
    key: 'hit_count',
    width: 100,
    render: nullableText,
  },
  {
    title: '最后命中词',
    dataIndex: 'last_hit_words',
    key: 'last_hit_words',
    width: 120,
    render: (value: string | null) => <div style={{ wordBreak: 'break-all', textAlign: 'left' }}>{nullableText(value)}</div>,
  },
  {
    title: '最后发言',
    dataIndex: 'last_hit_content',
    key: 'last_hit_content',
    width: 220,
  },
  {
    title: '最后命中时间',
    dataIndex: 'last_hit_time',
    key: 'last_hit_time',
    width: 180,
    render: formatDate,
  },
  {
    title: '禁言截止时间',
    dataIndex: 'mute_until',
    key: 'mute_until',
    width: 180,
    render: formatDate,
  },
  {
    title: '禁言原因',
    dataIndex: 'mute_reason',
    key: 'mute_reason',
    width: 220,
    render: (value: string | null) => <div style={{ wordBreak: 'break-all', textAlign: 'left' }}>{nullableText(value)}</div>,
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 110,
    render: (value: number | null) => renderDictTag(value, statusText),
  },
  {
    title: '处理状态',
    dataIndex: 'handle_status',
    key: 'handle_status',
    width: 110,
    render: (value: number | null) => renderDictTag(value, handleStatusText),
  },
];

export default function SensitiveRiskPage() {
  const [loading, setLoading] = useState(false);
  const [unmuteLoading, setUnmuteLoading] = useState(false);
  const [records, setRecords] = useState<SensitiveRiskUserRecord[]>([]);
  const [hasMute, setHasMute] = useState(false);
  const [queriedUserId, setQueriedUserId] = useState('');
  const [queried, setQueried] = useState(false);

  useEffect(() => {
    return () => {
      setRecords([]);
      setHasMute(false);
      setQueriedUserId('');
      setQueried(false);
    };
  }, []);

  async function loadInfo(userId: string) {
    setLoading(true);
    try {
      const resp = await User.sensitiveRiskGet(userId);
      setRecords(resp.records || []);
      setHasMute(resp.has_mute);
      setQueriedUserId(userId);
      setQueried(true);
    } catch (err) {
      notification.error({
        message: '查询失败',
        description: err instanceof Error ? err.message : '请稍后重试',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleQuery(values: QueryForm) {
    const userId = values.user_id?.trim();
    if (!userId) {
      notification.error({ message: '请输入 user_id' });
      return;
    }

    await loadInfo(userId);
  }

  async function handleUnmute() {
    if (!queriedUserId) {
      return;
    }

    setUnmuteLoading(true);
    try {
      const resp = await User.sensitiveRiskUnmute(queriedUserId);
      notification.success({
        message: '解除成功',
        description: `DB 更新 ${resp.db_rows_affected} 条，Redis 删除 ${resp.redis_deleted} 个 key`,
      });
      await loadInfo(queriedUserId);
    } catch (err) {
      notification.error({
        message: '解除失败',
        description: err instanceof Error ? err.message : '请稍后重试',
      });
    } finally {
      setUnmuteLoading(false);
    }
  }

  return (
    <div>
      <Row gutter={24} align="top">
        <Col span={10}>
          <Form<QueryForm>
            name="sensitiveRiskUser"
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            style={{ maxWidth: 720 }}
            onFinish={handleQuery}
            autoComplete="off"
          >
            <Form.Item<QueryForm> label="User ID" name="user_id" required>
              <Input placeholder="请输入 user_id" disabled={loading || unmuteLoading} />
            </Form.Item>

            <Form.Item label={null}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SearchOutlined />}>
                  查询
                </Button>
                <Popconfirm
                  title="确认解除风控发言禁言？"
                  description={queriedUserId ? `User ID: ${queriedUserId}` : ''}
                  okText="解除"
                  cancelText="取消"
                  onConfirm={handleUnmute}
                  okButtonProps={{ danger: true, loading: unmuteLoading }}
                >
                  <Button danger disabled={!queriedUserId || loading} loading={unmuteLoading} icon={<UnlockOutlined />}>
                    解除
                  </Button>
                </Popconfirm>
              </Space>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      {queried ? (
        <div style={{ margin: '24px 0' }}>
          <Descriptions
            bordered
            column={3}
            items={[
              {
                key: 'user_id',
                label: 'User ID',
                children: queriedUserId,
              },
              {
                key: 'has_mute',
                label: '禁言记录',
                children: hasMute ? <Tag color="red">存在</Tag> : <Tag color="green">不存在</Tag>,
              },
              {
                key: 'record_count',
                label: '风险记录总数',
                children: records.length,
              },
            ]}
          />
        </div>
      ) : null}

      {queried && records.length === 0 ? (
        <Typography.Text type="secondary">未查询到风险记录</Typography.Text>
      ) : (
        <Table<SensitiveRiskUserRecord>
          rowKey="id"
          loading={loading}
          dataSource={records}
          columns={columns}
          scroll={{ x: 1600 }}
          pagination={false}
        />
      )}
    </div>
  );
}
